import { useEffect, useState, useRef, useCallback } from "react";
import { ref, onValue, off } from "firebase/database";

// Fallback feed if news/activeSource is missing or invalid.
const DEFAULT_SOURCE = "wdwnt";

// Auto-rotate interval.
const ROTATE_MS = 10000;

// Source credit logos (top-right pill). Feeds without a logo show nothing.
const SOURCE_LOGOS = { wdwnt: "/static/wdwnt-logo.png" };

// Fallback image if an article has none.
function placeholderImg(title) {
  const t = encodeURIComponent((title || "News").slice(0, 28));
  return "https://dummyimage.com/1280x800/0b1220/ffffff&text=" + t;
}

const NewsHero = ({ database, style, showSource = true }) => {
  // Which feed to show — driven by news/activeSource in RTDB.
  const [activeSource, setActiveSource] = useState(DEFAULT_SOURCE);

  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  // Remember the current article's id so a feed refresh doesn't jump us.
  const currentIdRef = useRef(null);

  // ── Subscribe to news/activeSource ──────────────────────────────────
  useEffect(() => {
    if (!database) return;
    const srcRef = ref(database, "news/activeSource");

    const handler = onValue(srcRef, (snap) => {
      const v = String(snap.val() || "").toLowerCase();
      // Only accept known feeds; otherwise fall back to the default.
      setActiveSource(v === "wdwnt" || v === "mickey" ? v : DEFAULT_SOURCE);
    });

    return () => {
      off(srcRef);
      if (typeof handler === "function") handler();
    };
  }, [database]);

  // ── Subscribe to the news feed for the active source ────────────────
  // Re-runs whenever activeSource changes, so flipping news/activeSource in
  // RTDB swaps the feed live.
  useEffect(() => {
    if (!database) return;
    const itemsRef = ref(database, "news/" + activeSource + "/items");

    const handler = onValue(itemsRef, (snap) => {
      const raw = snap.val();

      let list = [];
      if (Array.isArray(raw)) list = raw.filter(Boolean);
      else if (raw && typeof raw === "object") list = Object.values(raw).filter(Boolean);

      list = list
        .map((x) => ({
          id: x?.id || "",
          title: x?.title || "",
          link: x?.link || "",
          pubDate: x?.pubDate || "",
          image: x?.image || null,
          summary: x?.summary || "",
          source: x?.source || activeSource,
        }))
        .filter((x) => x.id && x.title);

      setItems(list);

      // Keep showing the same article across feed refreshes if it survived.
      const currentId = currentIdRef.current;
      if (!list.length) {
        setIdx(0);
      } else if (currentId) {
        const found = list.findIndex((it) => it.id === currentId);
        setIdx(found >= 0 ? found : 0);
      } else {
        setIdx(0);
      }
    });

    return () => {
      off(itemsRef);
      if (typeof handler === "function") handler();
    };
  }, [database, activeSource]);

  // ── Current article ─────────────────────────────────────────────────
  const safeIdx = items.length ? Math.min(idx, items.length - 1) : 0;
  const current = items.length ? items[safeIdx] : null;
  currentIdRef.current = current?.id || null;

  // ── Advance to next article ─────────────────────────────────────────
  const nextArticle = useCallback(() => {
    setItems((list) => {
      if (list.length) setIdx((i) => (i + 1) % list.length);
      return list;
    });
  }, []);

  // ── Auto-rotate ─────────────────────────────────────────────────────
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!items.length) return;

    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % items.length);
    }, ROTATE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [idx, items.length]);

  const isLoading = !current;
  const bgImg = current ? current.image || placeholderImg(current.title) : null;

  return (
    <div
      style={{ ...cardStyle, ...style }}
      onClick={nextArticle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") nextArticle();
      }}
    >
      {isLoading ? (
        <>
          <style>{`
@keyframes wsNewsShimmer {
  0%   { background-position: -300px 0; }
  100% { background-position:  300px 0; }
}
@keyframes wsNewsDots {
  0%, 25%   { opacity: 0.25; }
  50%       { opacity: 1; }
  75%, 100% { opacity: 0.25; }
}
@keyframes wsNewsWave {
  0%   { transform: scale(0.25); opacity: 0; }
  15%  { opacity: 0.9; }
  100% { transform: scale(1); opacity: 0; }
}
`}</style>
          <div style={loadingBg} />
          <div style={loadingImageArea}>
            <div style={towerWrap}>
              <div style={{ ...signalArc, animationDelay: "0s" }} />
              <div style={{ ...signalArc, animationDelay: "0.7s" }} />
              <div style={{ ...signalArc, animationDelay: "1.4s" }} />
              <div style={towerSpike} />
              <div style={towerLegLeft} />
              <div style={towerLegRight} />
              <div style={towerStrutTop} />
              <div style={towerStrutMid} />
              <div style={towerBase} />
            </div>
            <div style={loadingMark}>
              Loading news
              <span style={{ ...loadingDot, animationDelay: "0s" }}>.</span>
              <span style={{ ...loadingDot, animationDelay: "0.18s" }}>.</span>
              <span style={{ ...loadingDot, animationDelay: "0.36s" }}>.</span>
            </div>
          </div>
          <div style={heroHeadline}>
            <div style={{ ...skelBar, width: "92%", height: 18 }} />
            <div style={{ ...skelBar, width: "61%", height: 18, marginTop: 12 }} />
          </div>
        </>
      ) : (
        <>
          <img src={bgImg} alt="" style={heroImg} />
          <div style={heroVignette} />
                    {showSource && SOURCE_LOGOS[current.source] && (
            <div style={sourcePill}>
              <span style={sourceLabel}>Source:</span>
              <img src={SOURCE_LOGOS[current.source]} alt="" style={sourceLogo} />
            </div>
          )}
          <div style={heroHeadline}>
            <div style={heroTitle}>{current.title}</div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────
const cardStyle = {
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: 22,
  overflow: "hidden",
  border: "1px solid rgba(11,31,53,0.20)",
  boxShadow: "0 14px 40px rgba(11,31,53,0.16)",
  background: "#000",
  minWidth: 0,
  cursor: "pointer",
};

const heroImg = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const heroVignette = {
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.22) 62%, rgba(0,0,0,0.40) 100%)",
  pointerEvents: "none",
};
const sourcePill = {
  position: "absolute",
  top: 14,
  right: 14,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 13px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.78)",
  pointerEvents: "none",
};
const sourceLabel = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 700,
  color: "#46506b",
};
const sourceLogo = { height: 20, width: "auto", display: "block" };
const heroHeadline = {
  position: "absolute",
  left: 16,
  right: 16,
  bottom: 16,
  borderRadius: 18,
  padding: "14px 16px",
  background: "rgba(255,255,255,0.94)",
  border: "1px solid rgba(11,31,53,0.20)",
  boxShadow: "0 14px 40px rgba(11,31,53,0.16)",
};

const heroTitle = {
  fontFamily: "Lato, sans-serif",
  fontSize: 25,
  fontWeight: 800,
  lineHeight: 1.12,
  letterSpacing: -0.3,
  color: "#0b1f35",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};
const loadingBg = {
  position: "absolute",
  inset: 0,
  background: "linear-gradient(160deg, #0b1f35 0%, #1a3054 55%, #0b1f35 100%)",
};

// Column stack: tower on top, caption underneath in NORMAL FLOW. The old
// version absolutely centered the rings and shoved the caption down with
// translateY(66px), so a growing ring ran straight through the text. Now
// the caption sits below the tower's own box and can't be reached.
const loadingImageArea = {
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 102,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 14,
};

// Broadcast tower — everything below is positioned inside this 96x96 box.
// Vertical map: spike 44-56, legs 56-90, base 90-93. Waves radiate up from
// y=44 (the spike tip) and top out at y=-20, well clear of the caption.
const towerWrap = {
  position: "relative",
  width: 96,
  height: 96,
  flexShrink: 0,
};

const TOWER_WHITE = "rgba(255,255,255,0.72)";

const towerSpike = {
  position: "absolute",
  left: "50%",
  marginLeft: -1,
  top: 44,
  width: 2,
  height: 12,
  background: TOWER_WHITE,
};

// Legs pivot from their TOP so they converge at the spike and splay out at
// the base — 13deg gives a ~18px footprint.
const towerLegBase = {
  position: "absolute",
  left: "50%",
  marginLeft: -1,
  top: 56,
  width: 2,
  height: 34,
  background: TOWER_WHITE,
  transformOrigin: "50% 0%",
};
const towerLegLeft = { ...towerLegBase, transform: "rotate(-13deg)" };
const towerLegRight = { ...towerLegBase, transform: "rotate(13deg)" };

const towerStrutTop = {
  position: "absolute",
  left: "50%",
  marginLeft: -5,
  top: 68,
  width: 10,
  height: 2,
  background: TOWER_WHITE,
};
const towerStrutMid = {
  position: "absolute",
  left: "50%",
  marginLeft: -8,
  top: 80,
  width: 16,
  height: 2,
  background: TOWER_WHITE,
};

const towerBase = {
  position: "absolute",
  left: "50%",
  marginLeft: -13,
  top: 90,
  width: 26,
  height: 3,
  borderRadius: 2,
  background: TOWER_WHITE,
};

// Signal wave — a full circle with only its TOP border painted, so what
// renders is an arc. transformOrigin at the box's bottom-center pins the
// arc's growth to the spike tip, so waves fan upward and outward from it.
const signalArc = {
  position: "absolute",
  left: "50%",
  marginLeft: -32,
  top: -20,
  width: 64,
  height: 64,
  borderRadius: "50%",
  border: "3px solid transparent",
  borderTopColor: "rgba(255,255,255,0.8)",
  transformOrigin: "50% 100%",
  animationName: "wsNewsWave",
  animationDuration: "2.1s",
  animationTimingFunction: "ease-out",
  animationIterationCount: "infinite",
  pointerEvents: "none",
};

const loadingMark = {
  position: "relative",
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 800,
  letterSpacing: 2.2,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.66)",
};

const loadingDot = {
  animationName: "wsNewsDots",
  animationDuration: "1.4s",
  animationTimingFunction: "ease-in-out",
  animationIterationCount: "infinite",
};

const skelBar = {
  height: 15,
  borderRadius: 8,
  background:
    "linear-gradient(90deg, rgba(11,31,53,0.09) 0%, rgba(11,31,53,0.20) 50%, rgba(11,31,53,0.09) 100%)",
  backgroundSize: "300px 100%",
  backgroundRepeat: "repeat-x",
  animationName: "wsNewsShimmer",
  animationDuration: "1.6s",
  animationTimingFunction: "linear",
  animationIterationCount: "infinite",
};

export default NewsHero;
